import { useCallback, useEffect, useRef, useState } from "react";
import { useUiStore } from "@/application/stores/uiStore";
import { MUSIC_CONSENT_IDLE_MS, shouldPromptMusicConsent } from "@/domain/settings/music-consent";
import { stopBgm } from "@/infrastructure/assets/audio";

/** Playwright 等で強制表示するときの sessionStorage キー。 */
export const MUSIC_CONSENT_FORCE_KEY = "vimquest.musicConsent.force";

/**
 * 自動操作（Playwright の `navigator.webdriver`）では既定で確認を出さない。
 * 専用E2Eだけ `sessionStorage` に force フラグを立てて表示する。
 */
export function shouldAutoShowMusicConsent(): boolean {
  if (typeof navigator === "undefined") {
    return true;
  }
  if (!navigator.webdriver) {
    return true;
  }
  try {
    return sessionStorage.getItem(MUSIC_CONSENT_FORCE_KEY) === "1";
  } catch {
    return false;
  }
}

/**
 * 囃子確認ポップアップの開閉と、最終操作から約30分での再表示を担う。
 * 「鳴らす」操作はブラウザの自動再生制限を解くユーザー操作にもなる。
 */
export function useMusicConsent(): {
  readonly open: boolean;
  readonly accept: () => void;
  readonly decline: () => void;
} {
  const setMuted = useUiStore((state) => state.setMuted);
  const [open, setOpen] = useState(() => shouldAutoShowMusicConsent());
  const decidedAtRef = useRef<number | null>(open ? null : Date.now());
  const lastActivityAtRef = useRef(Date.now());

  const bumpActivity = useCallback(() => {
    lastActivityAtRef.current = Date.now();
  }, []);

  useEffect(() => {
    const events: readonly (keyof DocumentEventMap)[] = [
      "pointerdown",
      "keydown",
      "touchstart",
      "wheel",
    ];
    for (const eventName of events) {
      document.addEventListener(eventName, bumpActivity, { passive: true });
    }
    return () => {
      for (const eventName of events) {
        document.removeEventListener(eventName, bumpActivity);
      }
    };
  }, [bumpActivity]);

  useEffect(() => {
    if (open) {
      return undefined;
    }
    const timer = window.setInterval(
      () => {
        if (shouldPromptMusicConsent(decidedAtRef.current, lastActivityAtRef.current, Date.now())) {
          stopBgm();
          setOpen(true);
        }
      },
      Math.min(30_000, MUSIC_CONSENT_IDLE_MS),
    );
    return () => window.clearInterval(timer);
  }, [open]);

  const accept = useCallback(() => {
    setMuted(false);
    const now = Date.now();
    decidedAtRef.current = now;
    lastActivityAtRef.current = now;
    setOpen(false);
  }, [setMuted]);

  const decline = useCallback(() => {
    setMuted(true);
    stopBgm();
    const now = Date.now();
    decidedAtRef.current = now;
    lastActivityAtRef.current = now;
    setOpen(false);
  }, [setMuted]);

  return { open, accept, decline };
}
